import { useEffect, useState } from "react";
import { formwizApi } from "./formwizApi";
import { eventBus } from "../../utils/eventBus";

/*
 * Form Inbox badge counts: {unreviewed, critical_unreviewed, emr_failed}, or
 * null (no badge). Polls every 60 s while the page is visible. Errors are
 * swallowed; a 404/403 (server without formwiz, practice not enabled) hides
 * the badge and stops polling for this page load.
 *
 * The side nav can remount between pages, so the last answer is shared at
 * module level: a mount within 60 s of the last fetch reuses it instead of
 * asking again (one request per page load in practice).
 */
const POLL_MS = 60 * 1000;
const shared = { at: 0, value: null, disabled: false, inflight: null };

// The inbox count is a staff route: never asked without a staff sign-in (a
// public page would get a 401, and api.js answers a 401 with refresh + a
// redirect to /login). Tablet mode keeps the token in memory behind the same
// localStorage.getItem("auth") (forms/patient/kioskLock.js).
const hasStaffToken = () => {
  try {
    return Boolean(window.localStorage.getItem("auth"));
  } catch (e) {
    return false;
  }
};

function fetchCount() {
  if (shared.inflight) return shared.inflight;
  shared.inflight = formwizApi
    .inboxCount()
    .then((d) => {
      const v = d && typeof d === "object" && !Array.isArray(d) ? d : {};
      shared.value = {
        unreviewed: Number(v.unreviewed) || 0,
        critical_unreviewed: Number(v.critical_unreviewed) || 0,
        emr_failed: Number(v.emr_failed) || 0,
      };
      shared.at = Date.now();
      return shared.value;
    })
    .catch((e) => {
      const st = e && e.response && e.response.status;
      if (st === 404 || st === 403) {
        shared.disabled = true;
        shared.value = null;
      }
      shared.at = Date.now();
      return shared.value;
    })
    .finally(() => {
      shared.inflight = null;
    });
  return shared.inflight;
}

// Tests only.
export function resetInboxCountCache() {
  shared.at = 0;
  shared.value = null;
  shared.disabled = false;
  shared.inflight = null;
}

export default function useInboxCount(enabled) {
  const [count, setCount] = useState(() => (enabled && !shared.disabled ? shared.value : null));

  useEffect(() => {
    if (!enabled || shared.disabled) return undefined;
    let alive = true;
    const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const run = (force) => {
      if (shared.disabled || isHidden() || !hasStaffToken()) return;
      if (!force && shared.at && Date.now() - shared.at < POLL_MS) {
        setCount(shared.value);
        return;
      }
      fetchCount().then((v) => {
        if (alive) setCount(shared.disabled ? null : v);
      });
    };
    run(false);
    const timer = setInterval(() => run(true), POLL_MS);
    const onVisible = () => {
      if (!isHidden()) run(false);
    };
    const onChanged = () => run(true);
    document.addEventListener("visibilitychange", onVisible);
    eventBus.on("formwiz:inbox-changed", onChanged);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      eventBus.off("formwiz:inbox-changed", onChanged);
    };
  }, [enabled]);

  return enabled ? count : null;
}
